import React, { useState, useEffect, useMemo } from 'react';
import './StorefrontHero.css';

/**
 * Integrated storefront hero card.
 *
 * One rounded card carries the whole hero. The merchant's photo
 * (or photos) sit in the back; the brand mark, headline, tagline
 * and CTAs are overlaid on top. Below the card sits a quiet trust
 * strip for value-density without competing for attention.
 *
 * Backdrop behavior:
 *   - heroSliderEnabled with multiple images → crossfading slider
 *     (5.5s per slide, 1s crossfade) + pagination dots inside the
 *     card for manual jumps.
 *   - Single image (heroImage, or slider-disabled with one image
 *     in the array) → static, no timer, no dots.
 *   - Slider off but heroSliderImages has items → falls back to
 *     the first slider image rather than pretend we have nothing.
 *   - No images at all → solid brand-colored backdrop.
 *
 * Dynamic text:
 *   - The merchant's heroDescription supports simple tokens —
 *     `{day}`, `{dayShort}`, `{date}`, `{month}`, `{monthShort}`,
 *     `{year}` — so a tagline like "Today's {day}" renders with
 *     the current day of the week.
 *   - A small "live" chip in the corner of the card shows the
 *     current day + date with a pulsing brand-color dot. Pure
 *     ambient detail; visitors can't interact with it.
 *
 * Two CTAs:
 *   - Primary "Shop Now"     → scrolls to the products catalog
 *   - Secondary "Categories" → scrolls to the categories section
 *
 * Parent (HomePage) owns the scroll targets via refs and passes
 * the click handlers in.
 */

const SLIDE_INTERVAL_MS = 5500;

/* ----------------------------------------------------------------
 * Date / token utilities
 * ---------------------------------------------------------------- */

// Builds the token table used by both the tagline interpolation
// and the live date chip. Locale is fixed to en-US for now —
// merchants might be in Nigeria but customer-facing storefront
// copy is overwhelmingly English-language, and bringing in i18n
// here would belong in a higher-level pass.
const buildDateTokens = (now = new Date()) => ({
  day:        now.toLocaleDateString('en-US', { weekday: 'long' }),   // "Friday"
  dayShort:   now.toLocaleDateString('en-US', { weekday: 'short' }),  // "Fri"
  month:      now.toLocaleDateString('en-US', { month: 'long' }),     // "May"
  monthShort: now.toLocaleDateString('en-US', { month: 'short' }),    // "May"
  date:       now.getDate(),                                          // 11
  year:       now.getFullYear()                                       // 2026
});

// Simple `{token}` substitution. Case-insensitive so a merchant
// who wrote `{Day}` still gets the right value. Unknown tokens
// are left untouched — letting them pass through is safer than
// silently stripping text the merchant intended.
const interpolateTokens = (text, tokens) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\{(\w+)\}/g, (match, key) => {
    const lookup = key.toLowerCase();
    for (const tokenKey of Object.keys(tokens)) {
      if (tokenKey.toLowerCase() === lookup) return String(tokens[tokenKey]);
    }
    return match;
  });
};

/* ----------------------------------------------------------------
 * Icons
 * ---------------------------------------------------------------- */

const ArrowRightIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const ShieldCheckIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="sf-hero-trust-icon">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const TruckIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="sf-hero-trust-icon">
    <path d="M10 17h4V5H2v12h3" />
    <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
    <circle cx="7.5" cy="17.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);

const LockIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="sf-hero-trust-icon">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/* ----------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------- */

const pickBackdropImages = (store) => {
  if (
    store?.heroSliderEnabled
    && Array.isArray(store?.heroSliderImages)
    && store.heroSliderImages.length > 0
  ) {
    return store.heroSliderImages;
  }
  if (store?.heroImage) {
    return [store.heroImage];
  }
  if (Array.isArray(store?.heroSliderImages) && store.heroSliderImages.length > 0) {
    return [store.heroSliderImages[0]];
  }
  return [];
};

const StorefrontHero = ({
  store,
  themeStyles,
  onShopClick,
  onBrowseClick
}) => {
  const primaryColor = themeStyles?.primary || '#111827';
  const storeName = store?.name || 'Our Store';

  // Computed once per mount. The page reloads frequently enough
  // for this not to drift in practice — we don't run a ticking
  // clock just to keep the day chip "live across midnight".
  const dateTokens = useMemo(() => buildDateTokens(), []);

  const tagline = useMemo(
    () => interpolateTokens(store?.heroDescription, dateTokens),
    [store?.heroDescription, dateTokens]
  );

  const backdropImages = useMemo(() => pickBackdropImages(store), [store]);
  const hasMultipleSlides = backdropImages.length > 1;

  const [activeIdx, setActiveIdx] = useState(0);

  // setTimeout (not setInterval) so manual dot clicks reset the
  // window — click a dot, get a fresh SLIDE_INTERVAL_MS before the
  // next auto-advance.
  useEffect(() => {
    if (!hasMultipleSlides) return undefined;
    const timer = setTimeout(() => {
      setActiveIdx(i => (i + 1) % backdropImages.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [hasMultipleSlides, backdropImages.length, activeIdx]);

  useEffect(() => {
    if (backdropImages.length > 0 && activeIdx >= backdropImages.length) {
      setActiveIdx(0);
    }
  }, [backdropImages.length, activeIdx]);

  return (
    <section className="sf-hero">
      <div className="sf-hero-card">
        <div className="sf-hero-card-bg">
          {backdropImages.length > 0 ? (
            backdropImages.map((img, idx) => (
              <img
                key={`${img}-${idx}`}
                src={img}
                alt=""
                className={`sf-hero-card-bg-slide${idx === activeIdx ? ' is-active' : ''}`}
                aria-hidden="true"
              />
            ))
          ) : (
            <div
              className="sf-hero-card-bg-fallback"
              style={{ backgroundColor: primaryColor }}
              aria-hidden="true"
            />
          )}
        </div>

        <div className="sf-hero-card-scrim" aria-hidden="true" />

        {/* Live-feel date chip — small pulsing dot + day/date.
            Ambient detail that signals the storefront is alive
            without claiming business hours we can't verify. */}
        <div className="sf-hero-card-date" aria-hidden="true">
          <span
            className="sf-hero-card-date-dot"
            style={{ backgroundColor: primaryColor }}
          />
          {dateTokens.day} · {dateTokens.monthShort} {dateTokens.date}
        </div>

        <div className="sf-hero-card-content">
          {store?.logo && (
            <img
              src={store.logo}
              alt=""
              className="sf-hero-logo"
              aria-hidden="true"
            />
          )}

          <h1 className="sf-hero-title">{storeName}</h1>

          {tagline && (
            <p className="sf-hero-tagline">{tagline}</p>
          )}

          <div className="sf-hero-actions">
            <button
              type="button"
              className="sf-hero-cta sf-hero-cta-primary"
              onClick={onShopClick}
            >
              Shop Now
              <ArrowRightIcon size={16} />
            </button>
            <button
              type="button"
              className="sf-hero-cta sf-hero-cta-secondary"
              onClick={onBrowseClick}
            >
              Categories
            </button>
          </div>
        </div>

        {hasMultipleSlides && (
          <div className="sf-hero-card-dots" role="tablist" aria-label="Hero slides">
            {backdropImages.map((_, idx) => (
              <button
                key={idx}
                type="button"
                role="tab"
                aria-selected={idx === activeIdx}
                aria-label={`Show slide ${idx + 1} of ${backdropImages.length}`}
                className={`sf-hero-card-dot${idx === activeIdx ? ' is-active' : ''}`}
                onClick={() => setActiveIdx(idx)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="sf-hero-trust" aria-label="Store guarantees">
        <span className="sf-hero-trust-item">
          <ShieldCheckIcon size={14} />
          Verified Store
        </span>
        <span className="sf-hero-trust-divider" aria-hidden="true" />
        <span className="sf-hero-trust-item">
          <TruckIcon size={14} />
          Fast Delivery
        </span>
        <span className="sf-hero-trust-divider" aria-hidden="true" />
        <span className="sf-hero-trust-item">
          <LockIcon size={14} />
          Secure Checkout
        </span>
      </div>
    </section>
  );
};

export default StorefrontHero;
